import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { isChallengeFocusEnabled } from '../utils/settings';

const GamificationContext = createContext();

const ME_API = window?.pfandpiratData?.meApiUrl || '/wp-json/pfandpirat/v1/me';
const XP_API = window?.pfandpiratData?.xpApiUrl || '/wp-json/pfandpirat/v1/xp';

function getHeaders(extra = {}) {
  return {
    'X-WP-Nonce': window?.pfandpiratData?.nonce || '',
    ...extra,
  };
}

function buildNoCacheUrl(url) {
  if (!url) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}_pfp_ts=${Date.now()}`;
}

function safeJsonParse(raw) {
  try {
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeSnapshot(raw, previous = null) {
  const prev = previous && typeof previous === 'object' ? previous : null;
  const source = raw && typeof raw === 'object' ? raw : {};

  const xp = toNumber(source.xp, prev?.xp ?? 0);
  const level = toNumber(source.level, prev?.level ?? 0);

  const achievementItems = Array.isArray(source.achievement_items)
    ? source.achievement_items
    : Array.isArray(prev?.achievement_items)
      ? prev.achievement_items
      : [];

  const achievements = Array.isArray(source.achievements)
    ? source.achievements
    : Array.isArray(prev?.achievements)
      ? prev.achievements
      : [];

  return {
    xp,
    level,
    rank: source.rank || prev?.rank || null,
    progress: source.progress || prev?.progress || null,
    achievement_count: toNumber(
      source.achievement_count,
      prev?.achievement_count ?? achievementItems.length ?? achievements.length ?? 0
    ),
    achievement_items: achievementItems,
    achievements,
  };
}

async function fetchJson(url, options = {}) {
  const method = String(options.method || 'GET').toUpperCase();
  const requestUrl = method === 'GET' ? buildNoCacheUrl(url) : url;

  const response = await fetch(requestUrl, {
    credentials: 'include',
    ...(method === 'GET' ? { cache: 'no-store' } : {}),
    ...options,
    headers: {
      ...getHeaders(),
      ...(options.headers || {}),
    },
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(`Request failed for ${url}`);
  }

  return data;
}

function normalizeRewardLoot(raw) {
  if (Array.isArray(raw)) {
    return raw
      .map((item) => {
        if (typeof item === 'string') {
          return item.trim();
        }

        if (item && typeof item === 'object') {
          return String(item.name || item.label || item.item_name || '').trim();
        }

        return '';
      })
      .filter(Boolean);
  }

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return [];

    if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
      const parsed = safeJsonParse(trimmed);
      if (parsed) {
        return normalizeRewardLoot(parsed);
      }
    }

    return trimmed
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (raw && typeof raw === 'object') {
    return normalizeRewardLoot([raw]);
  }

  return [];
}

function normalizePresentationItem(raw) {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const title = String(raw.popup_title || raw.title || raw.name || '').trim();
  if (!title) {
    return null;
  }

  const rewardLoot = normalizeRewardLoot(
    raw.reward_loot_items ?? raw.reward_loot ?? raw.rewardLootItems ?? raw.rewardLoot ?? []
  );

  return {
    key: String(raw.key || raw.presentation_key || raw.id || `${title}-${Date.now()}-${Math.random()}`),
    title,
    text: String(raw.popup_text || raw.text || raw.description || '').trim(),
    imageUrl: String(raw.imageUrl || raw.image_url || raw.badge_image_url || raw.graphic_url || '').trim(),
    soundUrl: String(raw.soundUrl || raw.sound_url || '').trim(),
    icon: String(raw.icon || raw.badge_icon || '🏅').trim() || '🏅',
    rarity: String(raw.rarity || 'common').trim() || 'common',
    presentationType: String(raw.presentationType || raw.presentation_type || 'achievement').trim() || 'achievement',
    popupEnabled: raw.popup_enabled === false || raw.popup_enabled === '0' ? false : true,
    rewardXp: toNumber(raw.reward_xp ?? raw.rewardXp, 0),
    rewardLoot,
    autoHideMs: toNumber(raw.autoHideMs ?? raw.auto_hide_ms, 4500),
    source: raw,
  };
}

function normalizeMomentRewardItems(raw) {
  if (!raw) return [];

  if (Array.isArray(raw)) {
    return raw
      .map((item) => {
        if (typeof item === 'string') {
          return { title: item.trim() };
        }

        if (item && typeof item === 'object') {
          return {
            title: String(item.title || item.name || item.label || item.item_name || '').trim(),
            imageUrl: String(item.imageUrl || item.image_url || item.badge_image_url || '').trim(),
            icon: String(item.icon || '').trim(),
          };
        }

        return null;
      })
      .filter((item) => item && item.title);
  }

  return normalizeMomentRewardItems([raw]);
}

function normalizeMomentItem(raw) {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const type = String(raw.type || raw.event || raw.presentation_type || raw.presentationType || 'reward').trim();
  const title = String(raw.title || raw.popup_title || raw.name || '').trim();

  if (!title) {
    return null;
  }

  const rawPresentation = String(raw.presentation || raw.display || raw.presentationMode || raw.presentation_mode || '').trim();
  const rarity = String(raw.rarity || raw.level || 'common').trim() || 'common';
  const rewards = raw.rewards && typeof raw.rewards === 'object' ? raw.rewards : {};
  const imageUrl = String(
    raw.imageUrl ||
    raw.image_url ||
    raw.badge_image_url ||
    raw.graphic_url ||
    raw.loot_image_url ||
    rewards.imageUrl ||
    rewards.image_url ||
    ''
  ).trim();

  const rewardLoot = normalizeMomentRewardItems(
    rewards.items ||
    rewards.loot ||
    raw.reward_items ||
    raw.reward_loot_items ||
    raw.rewardLootItems ||
    raw.rewardLoot ||
    []
  );

  const rewardXp = toNumber(rewards.xp ?? raw.reward_xp ?? raw.rewardXp, 0);
  const rewardCoins = toNumber(rewards.coins ?? raw.reward_coins ?? raw.rewardCoins, 0);
  const rewardCoinLabel = String(rewards.coin_label || rewards.coinLabel || raw.reward_coin_label || raw.rewardCoinLabel || '').trim();
  const rewardFragments = toNumber(rewards.fragments ?? raw.reward_fragments ?? raw.rewardFragments, 0);

  let presentation = rawPresentation;
  if (!presentation) {
    if (type === 'map_completed' || rarity === 'legendary') {
      presentation = 'fullscreen';
    } else if (rarity === 'rare' || rarity === 'epic') {
      presentation = 'popup';
    } else {
      presentation = 'toast';
    }
  }

  return {
    key: String(raw.key || raw.id || `${type}-${Date.now()}-${Math.random()}`),
    type,
    title,
    text: String(raw.message || raw.text || raw.description || raw.popup_text || '').trim(),
    icon: String(raw.icon || raw.badge_icon || '🎁').trim() || '🎁',
    imageUrl,
    rarity,
    presentation,
    priority: toNumber(raw.priority, presentation === 'fullscreen' ? 100 : presentation === 'popup' ? 50 : 10),
    sound: String(raw.sound || raw.sound_key || raw.soundKey || '').trim(),
    soundUrl: String(raw.soundUrl || raw.sound_url || '').trim(),
    animation: String(raw.animation || raw.animation_key || raw.animationKey || '').trim(),
    rewardXp,
    rewardCoins,
    rewardCoinLabel,
    rewardFragments,
    rewardLoot,
    autoHideMs: toNumber(raw.autoHideMs ?? raw.auto_hide_ms, presentation === 'toast' ? 3200 : presentation === 'popup' ? 5200 : 0),
    source: raw,
  };
}

function presentationToMoment(item) {
  if (!item) return null;

  const type = item.presentationType || 'achievement';
  const rarity = item.rarity || 'common';
  const isMajor = type === 'level_up' || type === 'map_completed' || rarity === 'legendary';

  return normalizeMomentItem({
    key: `presentation-${item.key}`,
    type,
    title: item.title,
    message: item.text,
    icon: item.icon,
    imageUrl: item.imageUrl,
    rarity,
    presentation: isMajor ? 'fullscreen' : 'popup',
    priority: isMajor ? 90 : 55,
    sound: item.soundUrl ? '' : type === 'achievement' ? 'achievement' : 'reward',
    soundUrl: item.soundUrl,
    animation: isMajor ? 'reward_burst' : 'loot_reveal',
    rewards: {
      xp: item.rewardXp,
      items: (item.rewardLoot || []).map((title) => ({ title })),
    },
    autoHideMs: item.autoHideMs,
  });
}

function appendUniquePresentations(previous, incoming) {
  if (!Array.isArray(incoming) || !incoming.length) {
    return previous;
  }

  const seen = new Set(previous.map((item) => String(item?.key || '')));
  const next = [...previous];

  incoming.forEach((item) => {
    const key = String(item?.key || '');
    if (!key || seen.has(key)) {
      return;
    }

    seen.add(key);
    next.push(item);
  });

  return next.sort((a, b) => Number(b?.priority || 0) - Number(a?.priority || 0));
}

function extractPresentationsFromResponse(payload) {
  if (!payload || typeof payload !== 'object') {
    return [];
  }

  const items = [];

  if (Array.isArray(payload.presentations)) {
    items.push(...payload.presentations);
  }

  if (payload.reward_presentation && typeof payload.reward_presentation === 'object') {
    items.push(payload.reward_presentation);
  }

  if (Array.isArray(payload.newly_awarded)) {
    items.push(...payload.newly_awarded);
  }

  if (Array.isArray(payload.newly_awarded_sync)) {
    items.push(...payload.newly_awarded_sync);
  }

  return items
    .map(normalizePresentationItem)
    .filter((item) => item && item.popupEnabled !== false);
}

function extractMomentsFromResponse(payload) {
  if (!payload || typeof payload !== 'object') {
    return [];
  }

  if (Array.isArray(payload.events) && payload.events.length) {
    return payload.events.map(normalizeMomentItem).filter(Boolean);
  }

  return extractPresentationsFromResponse(payload)
    .map(presentationToMoment)
    .filter(Boolean);
}

function firstString(...values) {
  for (const value of values) {
    const text = String(value || '').trim();
    if (text) return text;
  }
  return '';
}

function extractVisualMetaFromPayload(payload = {}, fallback = {}) {
  if (!payload || typeof payload !== 'object') {
    return fallback || {};
  }

  const toast = payload.xp_toast && typeof payload.xp_toast === 'object' ? payload.xp_toast : {};
  const loot = payload.loot && typeof payload.loot === 'object' ? payload.loot : {};
  const presentation = payload.reward_presentation && typeof payload.reward_presentation === 'object'
    ? payload.reward_presentation
    : {};

  return {
    icon: firstString(fallback.icon, toast.icon, loot.icon, presentation.icon),
    imageUrl: firstString(
      fallback.imageUrl,
      fallback.image_url,
      toast.imageUrl,
      toast.image_url,
      loot.imageUrl,
      loot.image_url,
      presentation.imageUrl,
      presentation.image_url,
      presentation.badge_image_url,
      presentation.graphic_url
    ),
  };
}

export function GamificationProvider({ children, initialSnapshot: providedInitialSnapshot = null }) {
  const initialSnapshot = normalizeSnapshot(providedInitialSnapshot || { xp: 0, level: 0 });
  const [data, setData] = useState(initialSnapshot);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [lastXpChange, setLastXpChange] = useState(null);
  const [momentQueue, setMomentQueue] = useState([]);
  const dataRef = useRef(initialSnapshot);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const persistSnapshot = useCallback((snapshot) => {
    const previous = dataRef.current || { xp: 0, level: 0 };
    const normalized = normalizeSnapshot(snapshot, previous);

    dataRef.current = normalized;
    setData(normalized);
    return normalized;
  }, []);

  const emitXpChange = useCallback((input = {}) => {
    const awarded = Number(input.awarded ?? 0);
    if (!Number.isFinite(awarded) || awarded === 0) {
      return null;
    }

    const xpValue = Number(input.xp);
    const nextXp = Number.isFinite(xpValue) ? xpValue : Number(dataRef.current?.xp || 0);

    const change = {
      id: Date.now() + Math.random(),
      event: String(input.event || ''),
      awarded,
      xp: nextXp,
      label: String(input.label || ''),
      icon: String(input.icon || '').trim(),
      imageUrl: String(input.imageUrl || input.image_url || '').trim(),
      amount_type: String(input.amount_type || input.amountType || '').trim(),
      amount_group: String(input.amount_group || input.amountGroup || '').trim(),
      item_type: String(input.item_type || input.itemType || '').trim(),
      post_id: input.post_id || input.postId || null,
      preview: Boolean(input.preview),
      eligible: input.eligible,
      account_hint: String(input.account_hint || input.accountHint || '').trim(),
    };

    setLastXpChange(change);
    return change;
  }, []);

  const queueMoment = useCallback((rawItem) => {
    const item = normalizeMomentItem(rawItem);
    if (!item) {
      return null;
    }

    setMomentQueue((prev) => appendUniquePresentations(prev, [item]));
    return item;
  }, []);

  const queueMoments = useCallback((items) => {
    if (!Array.isArray(items) || !items.length) {
      return [];
    }

    const normalized = items.map(normalizeMomentItem).filter(Boolean);
    if (!normalized.length) {
      return [];
    }

    setMomentQueue((prev) => appendUniquePresentations(prev, normalized));
    return normalized;
  }, []);

  const queuePresentation = useCallback((rawItem) => {
    const item = normalizePresentationItem(rawItem);
    if (!item || item.popupEnabled === false) {
      return null;
    }

    const moment = presentationToMoment(item);
    if (moment) {
      setMomentQueue((prev) => appendUniquePresentations(prev, [moment]));
    }

    return item;
  }, []);

  const queuePresentations = useCallback((items) => {
    if (!Array.isArray(items) || !items.length) {
      return [];
    }

    const normalized = items
      .map(normalizePresentationItem)
      .filter((item) => item && item.popupEnabled !== false);

    if (!normalized.length) {
      return [];
    }

    setMomentQueue((prev) => appendUniquePresentations(prev, normalized.map(presentationToMoment).filter(Boolean)));
    return normalized;
  }, []);

  const applyGamificationPayload = useCallback((payload, options = {}) => {
    const previous = dataRef.current || { xp: 0, level: 0 };
    const merged = persistSnapshot(normalizeSnapshot(payload || {}, previous));
    const suppressPresentations = isChallengeFocusEnabled();

    if (!suppressPresentations && !options.skipPresentations) {
      setMomentQueue((prev) => appendUniquePresentations(prev, extractMomentsFromResponse(payload)));
    }

    if (!suppressPresentations && options.emitXpToast) {
      const toastPayload = payload && typeof payload === 'object' && payload.xp_toast && typeof payload.xp_toast === 'object'
        ? payload.xp_toast
        : {};

      const visualMeta = extractVisualMetaFromPayload(payload, options);

      emitXpChange({
        event: options.event || toastPayload.event || payload?.event || '',
        awarded: options.awarded ?? toastPayload.awarded ?? payload?.awarded ?? 0,
        label: options.toastLabel || toastPayload.label || payload?.toast_label || '',
        icon: visualMeta.icon,
        imageUrl: visualMeta.imageUrl,
        xp: merged.xp,
        amount_type: toastPayload.amount_type || payload?.amount_type || '',
        amount_group: toastPayload.amount_group || payload?.amount_group || '',
        item_type: toastPayload.item_type || payload?.item_type || '',
        post_id: toastPayload.post_id || payload?.post_id || payload?.id || null,
        preview: Boolean(toastPayload.preview || payload?.preview),
        eligible: toastPayload.eligible ?? payload?.eligible,
        account_hint: toastPayload.account_hint || payload?.account_hint || '',
      });
    }

    return merged;
  }, [emitXpChange, persistSnapshot]);

  const dismissCurrentMoment = useCallback(() => {
    setMomentQueue((prev) => prev.slice(1));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    try {
      const meData = await fetchJson(ME_API);
      const merged = applyGamificationPayload(meData);
      setHasLoaded(true);
      return merged;
    } catch (error) {
      console.error('Pfandpirat /me konnte nicht geladen werden', error);
      setLoadError(error);
      return dataRef.current || initialSnapshot;
    } finally {
      setLoading(false);
    }
  }, [applyGamificationPayload]);

  const addXP = useCallback(async (input) => {
    let payload = {};
    let optimistic = 0;
    let explicitLabel = '';
    let requestedEvent = '';
    let emitXpToast = true;

    if (typeof input === 'number') {
      payload = { points: input };
      optimistic = input;
    } else if (typeof input === 'string') {
      payload = { event: input };
      requestedEvent = input;
    } else if (input && typeof input === 'object') {
      payload = {};
      if (input.event) {
        payload.event = input.event;
        requestedEvent = input.event;
      }
      if (input.points !== undefined) payload.points = input.points;
      optimistic = Number(input.optimistic ?? input.points ?? 0);
      explicitLabel = String(input.label || '');
      emitXpToast = input.emitXpToast !== false;
    } else {
      return null;
    }

    const currentData = dataRef.current || { xp: 0, level: 0 };

    if (optimistic !== 0) {
      persistSnapshot({
        ...currentData,
        xp: Math.max(0, Number(currentData.xp || 0) + optimistic),
      });
    }

    try {
      const json = await fetchJson(XP_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      applyGamificationPayload(json, {
        emitXpToast,
        event: json.event || requestedEvent || '',
        awarded: typeof json.awarded === 'number' ? json.awarded : optimistic,
        toastLabel: explicitLabel,
      });
      setHasLoaded(true);
      return json;
    } catch (error) {
      if (optimistic !== 0) {
        persistSnapshot(currentData);
      }
      setLoadError(error);
      return null;
    }
  }, [applyGamificationPayload, persistSnapshot]);

  const clearLastXpChange = useCallback(() => {
    setLastXpChange(null);
  }, []);

  const resetGamification = useCallback(() => {
    fetch('/wp-json/pfandpirat/v1/reset', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getHeaders(),
      },
    }).then(() => load());
  }, [load]);

  useEffect(() => {
    if (providedInitialSnapshot) {
      persistSnapshot(providedInitialSnapshot);
      setLoading(false);
      setHasLoaded(true);
      return;
    }

    load();
  }, [load, persistSnapshot, providedInitialSnapshot]);

  useEffect(() => {
    const handleFocus = () => {
      load();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        load();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [load]);

  const currentMoment = momentQueue.length ? momentQueue[0] : null;

  const value = useMemo(() => ({
    ...data,
    loading,
    hasLoaded,
    loadError,
    addXP,
    resetGamification,
    lastXpChange,
    clearLastXpChange,
    reloadGamification: load,
    applyGamificationPayload,
    emitXpChange,
    queuePresentation,
    queuePresentations,
    queueMoment,
    queueMoments,
    currentPresentation: currentMoment,
    dismissCurrentPresentation: dismissCurrentMoment,
    currentMoment,
    dismissCurrentMoment,
  }), [
    data,
    loading,
    hasLoaded,
    loadError,
    addXP,
    resetGamification,
    lastXpChange,
    clearLastXpChange,
    load,
    applyGamificationPayload,
    emitXpChange,
    queuePresentation,
    queuePresentations,
    queueMoment,
    queueMoments,
    currentMoment,
    dismissCurrentMoment,
  ]);

  return (
    <GamificationContext.Provider value={value}>
      {children}
    </GamificationContext.Provider>
  );
}

export function useGamification() {
  return useContext(GamificationContext);
}
